import { Colors, Slider, Text } from '@amelio/ui-library';

import {
    COMPENSATION_CAPTION,
    COMPENSATION_TILE_SURFACE,
    COMPENSATION_TILE_VALUE,
} from '../compensationStyles';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { formatDecimal } from '@/utils/numberFormat';

/** A threshold anywhere on the scale but the very top, and a spread exponent from "almost flat" to
 * "steeply in favour of the best". */
const THRESHOLD_MIN = 0;
const THRESHOLD_MAX = 4.5;
const SPREAD_MIN = 0.5;
const SPREAD_MAX = 3;
const SCORE_STEP = 0.1;

interface ScoreTileProps {
    label: string;
    value: string;
    help: string;
    result: string;
    minValue: number;
    maxValue: number;
    currentValue: number;
    canEdit: boolean;
    onChange: (value: number) => void;
    dataTestId: string;
}

/** One score-mode setting: its name, its current value, what it does, and what it does right now
 * to the people in the envelope. */
const ScoreTile = ({
    label,
    value,
    help,
    result,
    minValue,
    maxValue,
    currentValue,
    canEdit,
    onChange,
    dataTestId,
}: ScoreTileProps) => (
    <div
        className={`${COMPENSATION_TILE_SURFACE} flex flex-col gap-1`}
        data-testid={dataTestId}
    >
        <div className={'flex items-baseline justify-between gap-3'}>
            <Text
                weight={'bold'}
                color={Colors.NAVY}
                className={COMPENSATION_CAPTION}
            >
                {label}
            </Text>

            <Text
                color={Colors.NAVY}
                className={`${COMPENSATION_TILE_VALUE} whitespace-nowrap tabular-nums`}
            >
                {value}
            </Text>
        </div>

        <Text
            color={Colors.BASE_500}
            className={COMPENSATION_CAPTION}
        >
            {help}
        </Text>

        {/* Kept on screen when the envelope is read-only rather than removed: where the thumb sits
            is half of what the setting means, and a closed envelope is still read. */}
        <Slider
            trackTone={'light'}
            minValue={minValue}
            maxValue={maxValue}
            step={SCORE_STEP}
            currentValue={currentValue}
            onChange={onChange}
            showScale={false}
            ariaLabel={label}
            disabled={!canEdit}
            dataTestId={`${dataTestId}-slider`}
        />

        <Text
            color={Colors.BASE_500}
            className={COMPENSATION_CAPTION}
        >
            {result}
        </Text>
    </div>
);

interface ScoreTilesProps {
    scoreThreshold: number;
    scoreSpread: number;
    /** How many people the current threshold puts at zero, from the server's computation. */
    excludedByThresholdCount: number;
    /** How many times a 4.5 receives what a 3.0 receives; `null` when the threshold has climbed
     * past 3.0, which makes the comparison meaningless. */
    spreadRatio: number | null;
    canEdit: boolean;
    onThresholdChange: (value: number) => void;
    onSpreadChange: (value: number) => void;
}

/** The two settings of `Score` distribution mode: where eligibility starts, and how steeply the
 * budget tilts towards the best scores. */
export const ScoreTiles = ({
    scoreThreshold,
    scoreSpread,
    excludedByThresholdCount,
    spreadRatio,
    canEdit,
    onThresholdChange,
    onSpreadChange,
}: ScoreTilesProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const spreadResult =
        spreadRatio === null
            ? t('compensation.strategyCard.score.spreadResultNa')
            : t('compensation.strategyCard.score.spreadResult', {
                  ratio: formatDecimal(spreadRatio, language),
              });

    return (
        <div className={'grid grid-cols-1 gap-[16px] md:grid-cols-2'}>
            <ScoreTile
                label={t('compensation.strategyCard.score.thresholdLabel')}
                value={t('compensation.scoreOutOfFive', {
                    score: formatDecimal(scoreThreshold, language),
                })}
                help={t('compensation.strategyCard.score.thresholdHelp')}
                result={t('compensation.strategyCard.score.thresholdResult', {
                    count: excludedByThresholdCount,
                })}
                minValue={THRESHOLD_MIN}
                maxValue={THRESHOLD_MAX}
                currentValue={scoreThreshold}
                canEdit={canEdit}
                onChange={onThresholdChange}
                dataTestId={'compensation-score-threshold'}
            />

            <ScoreTile
                label={t('compensation.strategyCard.score.spreadLabel')}
                value={formatDecimal(scoreSpread, language)}
                help={t('compensation.strategyCard.score.spreadHelp')}
                result={spreadResult}
                minValue={SPREAD_MIN}
                maxValue={SPREAD_MAX}
                currentValue={scoreSpread}
                canEdit={canEdit}
                onChange={onSpreadChange}
                dataTestId={'compensation-score-spread'}
            />
        </div>
    );
};
